import {
  formatNumber,
  getGlowImageUrl,
  NftCollection,
  pluralize,
} from "@luma-team/shared";
import { LineClamp } from "@lux/atoms/components/LineClamp";
import { SquareNftImage } from "@glow/react/components/nfts/SquareNftImage";
import { SolAmount } from "@glow/react/components/SolAmount";
import { useOnScreen } from "@lux/atoms/hooks/useOnScreen";
import React, { useEffect, useRef, useState } from "react";

export const CollectionCard = ({ info }: { info: NftCollection.BasicInfo }) => {
  return (
    <div className="collection-card p-3">
      <div className="bg">
        <div
          className="img"
          style={
            info.image_url
              ? {
                  backgroundImage: `url(${getGlowImageUrl({
                    url: info.image_url,
                    width: 120,
                    height: 120,
                  })})`,
                }
              : {}
          }
        />
        <div className="glass" />
      </div>

      <div className="content">
        <SquareNftImage
          imageUrl={info.image_url}
          name={info.name}
          size={64}
          rounded
          className="nft-image"
        />

        <div className={"mt-2"}>
          <LineClamp
            lines={1}
            text={info.name}
            style={{
              fontWeight: "var(--font-weight-bold)",
            }}
          />
          <div className="count text-secondary fs-sm">
            {formatNumber({ num: info.num_nfts })}
            {pluralize(" NFT", info.num_nfts)}
          </div>
        </div>

        <div className="mt-2 fs-sm flex-center spread">
          <div className="flex-1">
            <div className="text-tertiary">30d Avg Price</div>
            <div className="value high-legibility">
              <SolAmount sol={info.avg_price_30d_sol!} />
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .collection-card {
          transform-origin: center;
          color: var(--primary-color);
          position: relative;
          transition: transform var(--transition-duration) var(--transition-fn),
            box-shadow var(--transition-duration) var(--transition-fn);
          border-radius: 1.5rem;
          overflow: hidden;
          transform: translateZ(-1px);
          border: 1px solid var(--secondary-border-color);

          :global(.nft-image) {
            border-radius: 0.65rem;
          }

          &:hover {
            transform: scale(1.02);
            box-shadow: var(--shadow);
          }

          .bg,
          .img,
          .glass {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            transform: translateZ(-1px);
          }

          .bg {
            .img {
              left: -3rem;
              top: -3rem;
              width: calc(100% + 6rem);
              height: calc(100% + 6rem);
              background-size: cover;
              background-position: center center;
              opacity: 0.5;
              filter: blur(70px);
            }

            .glass {
              background-color: rgb(255, 255, 255, 0.65);

              @media (prefers-color-scheme: dark) {
                background-color: rgb(50, 50, 50, 0.65);
              }
            }
          }

          .content {
            position: relative;
            transform: translateZ(-1px);
          }

          .value {
            font-weight: var(--font-weight-medium);
          }
        }
      `}</style>
    </div>
  );
};

export const CollectionLoadingCard = ({
  loadMore,
}: {
  loadMore?: () => void;
}) => {
  const loadMoreRef = useRef(null);
  const shouldLoadMore = useOnScreen(loadMoreRef);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (shouldLoadMore && loadMore && !loaded) {
      setLoaded(true);
      loadMore();
    }
  }, [shouldLoadMore, loaded, loadMore]);

  return (
    <div className="collection-card collection-loading-card shimmer-wrapper">
      <div className="p-3 m-0 card shimmer" ref={loadMoreRef}>
        <div className="inner">
          <div className="img" />
          <div className="mt-2">
            <div>...</div>
            <div className="fs-sm">...</div>
          </div>

          <div className="mt-2 fs-sm flex-center spread">
            <div className="flex-1">
              <div>...</div>
              <div>...</div>
            </div>
            <div className="flex-1">
              <div>...</div>
              <div>...</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .collection-card {
          .card {
            width: 100%;
            height: auto;
            border-radius: 1.5rem;
            border: 1px solid var(--secondary-border-color);

            .inner {
              visibility: hidden;
            }

            .img {
              width: 64px;
              height: 64px;
            }
          }
        }
      `}</style>
    </div>
  );
};
